import { useState, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { FieldErrors } from "react-hook-form";

import style from "./input.module.css";
import { colors } from "../../theme/colors";
import { guessTypeFromUrl } from "../../utils/common";

// Accept png/jpg/jpeg/svg+xml/svg/webp/gif
const imageTypeRegex = /^image\/(png|jpe?g|svg\+xml|svg|webp|gif)$/i;

interface InputProps {
  name: string;
  register: any;
  label?: string;
  error?: boolean;
  errors?: FieldErrors;
  required?: boolean;
  multiple?: boolean;
  height?: string;
  width?: string;
  borderRadius?: string;
  defaultValue?: string | File | Blob | null;
  onImageSelected?: (args: File[]) => void;
  value?: any;
}

const ImageInput = (props: InputProps) => {
  const {
    multiple = false,
    height = "250px",
    onImageSelected,
    width = "100%",
    borderRadius,
    defaultValue,
    label,
    required,
    name,
    error,
  } = props;

  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [images, setImages] = useState<string[]>([]); // data URLs for local uploads

  // Remote default URL -> blob URL
  const [defaultBlobSrc, setDefaultBlobSrc] = useState<string | undefined>(undefined);
  const lastDefaultBlob = useRef<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    const resolveDefault = async () => {
      // If a local preview exists, hide default
      if (images.length > 0) {
        if (!cancelled && lastDefaultBlob.current) {
          URL.revokeObjectURL(lastDefaultBlob.current);
          lastDefaultBlob.current = undefined;
        }
        setDefaultBlobSrc(undefined);
        return;
      }

      if (!defaultValue) {
        if (!cancelled && lastDefaultBlob.current) {
          URL.revokeObjectURL(lastDefaultBlob.current);
          lastDefaultBlob.current = undefined;
        }
        setDefaultBlobSrc(undefined);
        return;
      }

      // Already a data/blob URL
      if (typeof defaultValue === "string" && (/^data:/i.test(defaultValue) || /^blob:/i.test(defaultValue))) {
        setDefaultBlobSrc(defaultValue);
        return;
      }

      // Local File/Blob
      if (defaultValue instanceof Blob) {
        const obj = URL.createObjectURL(defaultValue);
        lastDefaultBlob.current = obj;
        setDefaultBlobSrc(obj);
        return;
      }

      // Remote URL
      if (typeof defaultValue === "string") {
        try {
          const res = await fetch(defaultValue, { credentials: "omit" });
          const ct = res.headers.get("content-type")?.toLowerCase() ?? "";

          if (!res.ok) {
            const peek = await res.clone().text().catch(() => "");
            console.error("[ImageInput] default image fetch failed:", res.status, ct, peek.slice(0, 200));
            if (!cancelled) setDefaultBlobSrc(undefined);
            return;
          }

          // Read the body once
          const rawBlob = await res.blob();

          // 1) Header says image/*
          if (ct.startsWith("image/")) {
            const isSvg = ct.includes("svg") || /\.svg(\?|$)/i.test(defaultValue);
            const type = isSvg ? "image/svg+xml" : (rawBlob.type || guessTypeFromUrl(defaultValue) || "image/png");
            const obj = URL.createObjectURL(new Blob([rawBlob], { type }));

            if (cancelled) return;
            if (lastDefaultBlob.current) URL.revokeObjectURL(lastDefaultBlob.current);
            lastDefaultBlob.current = obj;
            setDefaultBlobSrc(obj);
            return;
          }

          // 2) SVG often mislabeled as text/xml or text/plain -> read text and wrap
          if (ct.includes("svg") || ct.includes("xml") || ct.includes("text/")) {
            const text = await res.clone().text().catch(() => "");
            if (/^\s*<svg[\s>]/i.test(text)) {
              const obj = URL.createObjectURL(new Blob([text], { type: "image/svg+xml" }));
              if (cancelled) return;
              if (lastDefaultBlob.current) URL.revokeObjectURL(lastDefaultBlob.current);
              lastDefaultBlob.current = obj;
              setDefaultBlobSrc(obj);
              return;
            }
            console.error("[ImageInput] default text response is not SVG:", text.slice(0, 200));
            if (!cancelled) setDefaultBlobSrc(undefined);
            return;
          }

          // 3) Unknown type (e.g., octet-stream): pick best guess; if .svg, force svg+xml
          if (!rawBlob.size) {
            if (!cancelled) setDefaultBlobSrc(undefined);
            return;
          }
          const guessed = guessTypeFromUrl(defaultValue) || "image/png";
          const obj = URL.createObjectURL(new Blob([rawBlob], { type: guessed }));
          if (cancelled) return;
          if (lastDefaultBlob.current) URL.revokeObjectURL(lastDefaultBlob.current);
          lastDefaultBlob.current = obj;
          setDefaultBlobSrc(obj);
        } catch (e) {
          console.error("[ImageInput] default fetch error:", e);
          if (!cancelled) setDefaultBlobSrc(undefined);
        }
      }
    }

    resolveDefault();
    return () => {
      cancelled = true;
      if (lastDefaultBlob.current) {
        URL.revokeObjectURL(lastDefaultBlob.current);
        lastDefaultBlob.current = undefined;
      }
    };
  }, [defaultValue, images.length]);

  // Local upload preview via FileReader (unchanged)
  const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = Array.from(e.target.files ?? []);
    const valid = fileList.filter(f => imageTypeRegex.test(f.type));
    if (valid.length) {
      setImageFiles(valid);
      onImageSelected?.(valid);
    }
  };

  useEffect(() => {
    const nextImages: string[] = [];
    const readers: FileReader[] = [];
    let cancelled = false;

    if (imageFiles.length) {
      imageFiles.forEach((file) => {
        const r = new FileReader();
        readers.push(r);
        r.onload = (ev) => {
          const result = (ev.target as FileReader).result as string | null;
          if (result) nextImages.push(result);
          if (!cancelled && nextImages.length === imageFiles.length) {
            setImages(nextImages);
          }
        };
        r.readAsDataURL(file);
      });
    } else {
      setImages([]);
    }

    return () => {
      cancelled = true;
      readers.forEach(r => {
        if (r.readyState === 1) r.abort();
      });
    };
  }, [imageFiles]);

  return (
    <Box sx={{ flex: 1, flexDirection: "column" }}>
      <Box
        className={style.imageField}
        sx={{
          flex: 1,
          width,
          minHeight: height,
          maxHeight: height,
          borderRadius: borderRadius ?? 8,
          borderColor: error && required ? colors.red : "#C8C8C8",
          position: "relative",
        }}
      >
        <input
          key={name}
          type="file"
          onChange={changeHandler}
          accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
          style={{
            position: "absolute",
            inset: 0,
            minHeight: height,
            maxHeight: height,
            width,
            maxWidth: width,
            opacity: 0,
            borderRadius: borderRadius ?? 8,
            cursor: "pointer",
          }}
          multiple={multiple}
          aria-label={label ?? name}
        />

        {images.length > 0 ? (
          <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", borderRadius: borderRadius ?? 12 }}>
            {images.map((image, idx) => (
              <img
                key={`${name}-${idx}`}
                src={image}
                alt={label ? `${label} preview ${idx + 1}` : "Selected image preview"}
                style={{
                  padding: 5,
                  minHeight: height,
                  maxHeight: height,
                  width,
                  maxWidth: width,
                  objectFit: "contain",
                  overflow: "hidden",
                }}
              />
            ))}
          </Box>
        ) : defaultBlobSrc ? (
          <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", borderRadius: borderRadius ?? 12 }}>
            <img
              src={defaultBlobSrc}
              alt={label ?? "Default image"}
              onError={(e) => {
                console.error("[ImageInput] default image failed", defaultValue);
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
              style={{
                padding: 5,
                minHeight: height,
                maxHeight: height,
                maxWidth: width,
                objectFit: "contain",
                overflow: "hidden",
              }}
            />
          </Box>
        ) : null}
      </Box>

      {error && required && (
        <Typography sx={{ color: colors.red, fontSize: "12px" }}>
          {`* ${label} is required`}
        </Typography>
      )}
    </Box>
  );
};

export default ImageInput;
